import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Login as LoginIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.message || 'Verification failed. The link may be invalid or expired.');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
        px: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          backgroundColor: 'rgba(26, 26, 46, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          textAlign: 'center',
          p: 4
        }}
      >
        <CardContent>
          {status === 'verifying' && (
            <>
              <Box sx={{ mb: 3 }}>
                <CircularProgress size={60} sx={{ color: '#6366f1' }} />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                Verifying Your Email...
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Please wait while we verify your email address.
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <SuccessIcon sx={{ fontSize: 40, color: '#fff' }} />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                Email Verified! ✅
              </Typography>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                {message}
              </Alert>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <strong>What happens next?</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>An administrator will review your registration</li>
                  <li>You'll receive an email when your account is approved</li>
                  <li>Then you can login and start using the system</li>
                </ul>
              </Alert>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                startIcon={<LoginIcon />}
                sx={{
                  py: 1.5,
                  px: 4,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  }
                }}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <ErrorIcon sx={{ fontSize: 40, color: '#fff' }} />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {message}
              </Alert>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                The verification link may have expired or is invalid. Please try signing up again.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/signup')}
                  startIcon={<EmailIcon />}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: '#fff'
                  }}
                >
                  Sign Up Again
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/login')}
                  startIcon={<LoginIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailVerification;
